<template>
  <div
    v-loading="loading"
    class="max-w-360 m-auto w-full h-full flex flex-col items-center justify-center"
  >
    <div class="flex-1 flex items-center">
      <ScreenCarousel
        v-model:page="activePage"
        :items="carouselItems"
        :is-edit="false"
        :upload-disabled="!hasPendingChanges"
        @upload="onUploadDisplay"
      />
    </div>

    <Tabs
      v-model="activeTabValue"
      class="shrink-0 mb-2 w-full h-82 min-h-fit flex flex-col items-center gap-4.5"
    >
      <TabsList class="p-0 flex items-center gap-4 h-10">
        <TabsTrigger v-for="tab in tabs" :key="tab.value" :value="tab.value">
          <Button color="glass" :active="tab.value === activeTabValue" class="h-10">
            {{ $t(tab.label) }}
          </Button>
        </TabsTrigger>
      </TabsList>

      <Transition
        name="fade"
        mode="out-in"
        enter-active-class="animate-in fade-in duration-150"
        leave-active-class="animate-out fade-out duration-150"
      >
        <component :is="activeTabComponent" :pages="activeTabPages" v-on="tabCompEvents" />
      </Transition>
    </Tabs>

    <Dialog
      v-model:open="isUploadConfirmOpen"
      :title="$t('pageAndPlayer.pages.side.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('pageAndPlayer.routeDialog.description') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="confirmLeave">
            {{ $t('pageAndPlayer.routeDialog.cancelText') }}
          </Button>
          <Button color="black" @click="confirmUpload">
            {{ $t('pageAndPlayer.routeDialog.confirmText') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, markRaw, defineAsyncComponent } from 'vue'
  import { Button, Dialog, Tabs, TabsList, TabsTrigger, toast } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import ScreenCarousel from '../components/ScreenCarousel.vue'
  import { usePlaybackSetting } from '../composables/usePlaybackSetting'
  import { useUploadConfirmOnLeave } from '../composables/useUploadConfirmOnLeave'
  import { useRestoreChildDevice } from '../composables/useRestoreChildDevice'
  import type { PageItem } from '../utils/types'

  const { t } = useLocaleStore()
  useRestoreChildDevice()

  const tabs = [
    {
      label: 'pageAndPlayer.player.tabDisplay',
      value: 'custom',
      component: markRaw(defineAsyncComponent(() => import('./components/TabCustom.vue'))),
    },
    {
      label: 'pageAndPlayer.player.tabSort',
      value: 'sort',
      component: markRaw(defineAsyncComponent(() => import('./components/TabSort.vue'))),
    },
  ]

  const {
    activePage,
    carouselItems,
    toolbarPages,
    sortPages,
    hasPendingChanges,
    loading,
    togglePageDisplay,
    setPageDelay,
    reorderSortPages,
    setPageDisplay,
  } = usePlaybackSetting()

  const activeTabValue = ref('custom')

  const activeTabComponent = computed(() => {
    return tabs.find((tab) => tab.value === activeTabValue.value)?.component
  })

  const activeTabPages = computed(() => {
    return activeTabValue.value === 'sort' ? sortPages.value : toolbarPages.value
  })

  const tabCompEvents = computed(() => {
    switch (activeTabValue.value) {
      case 'custom':
        return {
          select: onToggleSelect,
          'update:delay': onUpdateDelay,
        }
      case 'sort':
        return {
          'update:order': onUpdateSortOrder,
        }
      default:
        return {}
    }
  })

  const onToggleSelect = (slot: number) => {
    const result = togglePageDisplay(slot)

    if (result === 'min-one') {
      toast.info(t('pageAndPlayer.player.minDisplay'))
    }
  }

  const onUpdateDelay = (slot: number, delay: number) => {
    setPageDelay(slot, delay)
  }

  const onUploadDisplay = () => {
    setPageDisplay()
  }

  // 当 upload 可用时离开页面会弹出确认弹窗：离开 / 上传
  const {
    isConfirmDialogOpen: isUploadConfirmOpen,
    confirmLeave,
    confirmUpload,
  } = useUploadConfirmOnLeave(hasPendingChanges, onUploadDisplay)

  const onUpdateSortOrder = (orderedPages: PageItem[]) => {
    reorderSortPages(orderedPages)
  }
</script>
